import React, { useState, useEffect, useRef } from 'react';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import EmailInput from '../ui/EmailInput';
import { useHubspot } from '../../hooks/useHubspot';
import { validateEmail } from '../../utils/validation';
import { marketingParams } from '../../utils/marketingParams';

type EmailFormProps = {
  formData: FormData;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function EmailForm({ formData, onChange, onSubmit }: EmailFormProps) {
  const [error, setError] = useState<string | null>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const hubspot = useHubspot();
  const params = marketingParams.getParams();
  
  useEffect(() => {
    // Focus the email input when the component mounts
    emailInputRef.current?.focus();
  }, []);

  // Validate email on change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    const validationError = emailValue ? validateEmail(emailValue) : null;
    setError(validationError);
    onChange(e);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    const validationError = validateEmail(formData.email);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      // Track email submission in HubSpot analytics
      hubspot.identify({
        email: formData.email,
        ...params,
      });

      // Track form step completion
      hubspot.trackEvent('email_step_completed', {
        email: formData.email,
        form_step: 'email',
        ...params
      });

      onSubmit({ email: formData.email });
    } catch (err) {
      console.error('Failed to submit HubSpot form:', err);
      // Continue with form submission even if HubSpot form fails
      onSubmit({ email: formData.email });
    }
  };

  // Check if the button should be disabled
  const isButtonDisabled = !formData.email || !!error;

  return (
    <form id="email-form" name="email-form" onSubmit={handleSubmit} className="space-y-6">
      <div className="text-center">
        <Title 
          id="email-title"
          name="email-title"
          as="h1" 
          className="mb-2"
        >
          What's your email?
        </Title>
        <Description 
          id="email-description"
          name="email-description"
          size="lg"
        >
          We'll use this to send you important updates about your application
        </Description>
      </div>

      <EmailInput
        ref={emailInputRef}
        id="email-input"
        name="email-input"
        value={formData.email}
        onChange={handleEmailChange}
        error={error}
      />

      {/* Hidden marketing tracking fields */}
      {Object.entries(params).map(([key, value]) => (
        <input
          key={key}
          type="hidden"
          name={key}
          value={value}
          data-hs-field-hidden="true"
        />
      ))}

      <Button
        id="email-submit"
        name="email-submit"
        type="submit"
        fullWidth
        size="lg"
        disabled={isButtonDisabled}
      >
        Continue
      </Button>
    </form>
  );
}